import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Send, UserPlus, CheckCircle2 } from 'lucide-react';

const PURPOSES = ['merchant_settlement', 'intergroup_transfer', 'financial_services', 'goods_sold', 'goods_bought', 'professional', 'personal_expenses', 'salaries', 'charity', 'none'];

const statusColor = (s) => {
  if (s === 'COMPLETED' || s === 'ACTIVE') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (s === 'PENDING' || s === 'PROCESSING' || s === 'UNDER_REVIEW') return 'bg-amber-50 text-amber-800 border-amber-200';
  return 'bg-red-50 text-red-700 border-red-200';
};

const RelmBankingRail = () => {
  const [env, setEnv] = useState('sandbox');
  const [overview, setOverview] = useState(null);
  const [bens, setBens] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showBenForm, setShowBenForm] = useState(false);
  const [ben, setBen] = useState({ name: '', reference: 'EzyTransfer settlement', account_number: '', routing_number: '', bic_swift_code: '', iban: '', transaction_type: 'LOCAL', type: 'BUSINESS', country_code: 'US', bank_country_code: 'US' });
  const [pay, setPay] = useState({ beneficiary_id: '', amount: '', purpose: 'merchant_settlement' });
  const [estimate, setEstimate] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (e) => {
    setLoading(true);
    setEstimate(null);
    try {
      const [o, b, p] = await Promise.allSettled([
        api.get(`/relm/banking/overview?env=${e}`),
        api.get(`/relm/banking/beneficiaries?env=${e}`),
        api.get(`/relm/banking/payouts?env=${e}`),
      ]);
      if (o.status === 'fulfilled') setOverview(o.value.data); else setOverview(null);
      if (b.status === 'fulfilled') setBens(b.value.data.beneficiaries || []); else setBens([]);
      if (p.status === 'fulfilled') setPayouts(p.value.data.payouts || []); else setPayouts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(env); }, [env, load]);

  const createBen = async () => {
    if (!ben.name || (!ben.account_number && !ben.iban)) { toast.error('Name and account number (or IBAN) required'); return; }
    setBusy(true);
    try {
      const r = await api.post('/relm/banking/beneficiary', { ...ben, env });
      toast.success(`Beneficiary created — status ${r.data.beneficiary?.status}`);
      setShowBenForm(false);
      load(env);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Beneficiary creation failed');
    } finally { setBusy(false); }
  };

  const confirmBen = async (id) => {
    setBusy(true);
    try {
      await api.put(`/relm/banking/beneficiary/${id}/confirm?env=${env}`);
      toast.success('Beneficiary confirmed');
      load(env);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Confirm failed');
    } finally { setBusy(false); }
  };

  const doEstimate = async () => {
    if (!pay.beneficiary_id || !pay.amount) { toast.error('Select beneficiary and amount'); return; }
    setBusy(true);
    try {
      const r = await api.post('/relm/banking/payout', { ...pay, amount: parseFloat(pay.amount), env, confirm: false });
      setEstimate(r.data.estimate);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Estimate failed');
    } finally { setBusy(false); }
  };

  const execute = async () => {
    setBusy(true);
    try {
      const r = await api.post('/relm/banking/payout', { ...pay, amount: parseFloat(pay.amount), env, confirm: true });
      toast.success(`Payout ${r.data.payout.payout_id} — ${r.data.payout.status}`);
      setEstimate(null);
      setPay({ beneficiary_id: '', amount: '', purpose: 'merchant_settlement' });
      load(env);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Payout failed');
    } finally { setBusy(false); }
  };

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/relm/banking/payouts/refresh?env=${env}`);
      toast.success(`Checked ${r.data.checked} — ${r.data.updates.length} status change(s)`);
      load(env);
    } catch (e) {
      toast.error('Refresh failed');
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-6 border-cyan-200" data-testid="relm-payout-rail-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">Banking Rail — Payouts</h3>
          <p className="text-xs text-slate-500">Official RELM spec: beneficiary → feeHash → payout (idempotent) → poll status</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={env} onChange={(e) => setEnv(e.target.value)} className="border rounded px-2 py-1 text-xs" data-testid="relm-rail-env-select">
            <option value="sandbox">SANDBOX</option>
            <option value="production">PRODUCTION</option>
          </select>
          <Button variant="ghost" size="sm" onClick={() => load(env)} disabled={loading} data-testid="relm-rail-reload-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {overview && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="relm-rail-balances">
          {(overview.balances || []).filter((b) => ['USD', 'USDC', 'USDT'].includes(b.currency)).map((b) => (
            <Badge key={b.currency} variant="outline" className="font-mono text-[11px]" data-testid={`relm-rail-balance-${b.currency}`}>
              {b.currency} {Number(b.available || 0).toFixed(2)}
            </Badge>
          ))}
          <span className="text-[10px] text-slate-400 self-center font-mono">acct {String(overview.account_id || '').slice(0, 8)}…</span>
        </div>
      )}

      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold">Beneficiaries</h4>
          <Button variant="outline" size="sm" onClick={() => setShowBenForm(!showBenForm)} data-testid="relm-ben-toggle-btn">
            <UserPlus className="w-3 h-3 mr-1" /> New beneficiary
          </Button>
        </div>
        {showBenForm && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-3 bg-slate-50 rounded mb-2" data-testid="relm-ben-form">
            <div><Label className="text-[10px]">Name</Label><Input value={ben.name} onChange={(e) => setBen({ ...ben, name: e.target.value })} data-testid="relm-ben-name-input" /></div>
            <div><Label className="text-[10px]">Account number</Label><Input value={ben.account_number} onChange={(e) => setBen({ ...ben, account_number: e.target.value })} data-testid="relm-ben-account-input" /></div>
            <div><Label className="text-[10px]">Routing (ACH)</Label><Input value={ben.routing_number} onChange={(e) => setBen({ ...ben, routing_number: e.target.value })} data-testid="relm-ben-routing-input" /></div>
            <div><Label className="text-[10px]">BIC/SWIFT (intl)</Label><Input value={ben.bic_swift_code} onChange={(e) => setBen({ ...ben, bic_swift_code: e.target.value })} data-testid="relm-ben-bic-input" /></div>
            <div>
              <Label className="text-[10px]">Routing type</Label>
              <select value={ben.transaction_type} onChange={(e) => setBen({ ...ben, transaction_type: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="relm-ben-txtype-select">
                <option value="LOCAL">LOCAL (ACH)</option>
                <option value="INTERNATIONAL">INTERNATIONAL (SWIFT)</option>
              </select>
            </div>
            <div>
              <Label className="text-[10px]">Entity</Label>
              <select value={ben.type} onChange={(e) => setBen({ ...ben, type: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="relm-ben-type-select">
                <option value="BUSINESS">BUSINESS</option>
                <option value="INDIVIDUAL">INDIVIDUAL</option>
              </select>
            </div>
            <div className="col-span-2 md:col-span-3">
              <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700" onClick={createBen} disabled={busy} data-testid="relm-ben-create-btn">
                {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <UserPlus className="w-3 h-3 mr-1" />} Create beneficiary
              </Button>
            </div>
          </div>
        )}
        {bens.length === 0 ? (
          <p className="text-xs text-slate-500" data-testid="relm-bens-empty">No beneficiaries yet on {env}.</p>
        ) : (
          <table className="w-full text-xs" data-testid="relm-bens-table">
            <thead className="text-[10px] uppercase text-slate-500 bg-slate-50"><tr><th className="text-left px-2 py-1">Name</th><th className="text-left px-2 py-1">Account</th><th className="text-left px-2 py-1">Status</th><th className="px-2 py-1"></th></tr></thead>
            <tbody>
              {bens.map((b) => (
                <tr key={b.id} className="border-t border-slate-100" data-testid={`relm-ben-row-${b.id}`}>
                  <td className="px-2 py-1.5">{b.name}</td>
                  <td className="px-2 py-1.5 font-mono text-[10px]">{b.accountNumber || b.iban} {b.routingNumber ? `/ ${b.routingNumber}` : ''} {b.bicSwiftCode ? `/ ${b.bicSwiftCode}` : ''}</td>
                  <td className="px-2 py-1.5"><Badge className={`text-[10px] border ${statusColor(b.status)}`}>{b.status}</Badge></td>
                  <td className="px-2 py-1.5">
                    {b.status === 'PENDING' && (
                      <Button variant="outline" size="sm" onClick={() => confirmBen(b.id)} disabled={busy} data-testid={`relm-ben-confirm-${b.id}`}>
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Confirm
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="mb-5 p-3 bg-cyan-50/50 border border-cyan-100 rounded" data-testid="relm-payout-form">
        <h4 className="text-sm font-semibold mb-2">Send payout</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div>
            <Label className="text-[10px]">Beneficiary (ACTIVE)</Label>
            <select value={pay.beneficiary_id} onChange={(e) => { setPay({ ...pay, beneficiary_id: e.target.value }); setEstimate(null); }} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="relm-payout-ben-select">
              <option value="">Select…</option>
              {bens.filter((b) => b.status === 'ACTIVE').map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div><Label className="text-[10px]">Amount USD</Label><Input type="number" value={pay.amount} onChange={(e) => { setPay({ ...pay, amount: e.target.value }); setEstimate(null); }} data-testid="relm-payout-amount-input" /></div>
          <div>
            <Label className="text-[10px]">Purpose</Label>
            <select value={pay.purpose} onChange={(e) => setPay({ ...pay, purpose: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="relm-payout-purpose-select">
              {PURPOSES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <Button size="sm" variant="outline" onClick={doEstimate} disabled={busy} data-testid="relm-payout-estimate-btn">Estimate</Button>
            <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700" onClick={execute} disabled={busy || !estimate} data-testid="relm-payout-execute-btn">
              {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />} Execute
            </Button>
          </div>
        </div>
        {estimate && (
          <p className="text-xs text-cyan-900 mt-2 font-mono" data-testid="relm-payout-estimate-result">
            Fee: {estimate.fee} — Total debit: {estimate.total_amount} — ETA: {String(estimate.estimated_date || '').slice(0, 10)}
          </p>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold">Payouts</h4>
          <Button variant="outline" size="sm" onClick={refresh} disabled={busy} data-testid="relm-payouts-refresh-btn">
            <RefreshCw className="w-3 h-3 mr-1" /> Poll status
          </Button>
        </div>
        {payouts.length === 0 ? (
          <p className="text-xs text-slate-500" data-testid="relm-payouts-empty">No payouts yet on {env}.</p>
        ) : (
          <table className="w-full text-xs" data-testid="relm-payouts-table">
            <thead className="text-[10px] uppercase text-slate-500 bg-slate-50"><tr><th className="text-left px-2 py-1">Payout ID</th><th className="text-left px-2 py-1">Beneficiary</th><th className="text-right px-2 py-1">Amount</th><th className="text-right px-2 py-1">Fee</th><th className="text-left px-2 py-1">Status</th><th className="text-left px-2 py-1">Created</th></tr></thead>
            <tbody>
              {payouts.map((p) => (
                <tr key={p.id} className="border-t border-slate-100" data-testid={`relm-payout-row-${p.payout_id}`}>
                  <td className="px-2 py-1.5 font-mono text-[10px]">{String(p.payout_id || '').slice(0, 13)}…</td>
                  <td className="px-2 py-1.5">{p.beneficiary_name || p.beneficiary_id?.slice(0, 8)}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{Number(p.amount).toFixed(2)} {p.currency}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{p.fee_amount ?? '-'}</td>
                  <td className="px-2 py-1.5"><Badge className={`text-[10px] border ${statusColor(p.status)}`}>{p.status}</Badge></td>
                  <td className="px-2 py-1.5 font-mono text-[10px]">{String(p.created_at || '').slice(0, 16)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Card>
  );
};

export default RelmBankingRail;
